import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  FileText, 
  Globe,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
  RefreshCw,
  Sparkles,
  ExternalLink,
  ChevronLeft
} from 'lucide-react';
import IconRefreshCw from '@/components/icons/atoms/IconRefreshCw';
import IconCopy from '@/components/icons/atoms/IconCopy';
import IconDownload from '@/components/icons/atoms/IconDownload';
import IconEdit from '@/components/icons/atoms/IconEdit';
import { useWebsites } from '@/context/WebsitesContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import AppDrawer from '@/components/ui/AppDrawer';
import { DrawerContent, DrawerFooter } from '@/components/ui/drawer-utils';
import { prepareV3OnboardingRequest } from '@/utils/prepareV3Onboarding';
import { ContentTag } from '@/components/ui/content-tag';
import { getTemplateInfo, getLengthTagInfo } from '@/lib/contentHelpers';
import { supabase } from '@/integrations/supabase/client';
// no auth requirement for onboarding write – use anon key

// Helper function to format titles with proper Danish capitalization
const formatDanishTitle = (title: string): string => {
  // Danish titles only capitalize the first word and proper nouns
  // This is a simplified approach without proper noun detection

  // Split the title into words
  const words = title.split(" ");

  // Capitalize first word
  if (words.length > 0) {
    words[0] =
      words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase();
  }

  // Keep rest of words lowercase, except potential proper nouns
  // For proper implementation, this would need NLP to detect proper nouns
  for (let i = 1; i < words.length; i++) {
    // Simple heuristic: keep words that were originally capitalized as capitalized
    // This isn't perfect but helps retain proper nouns
    if (words[i].length > 0 && words[i][0] === words[i][0].toUpperCase()) {
      // This might be a proper noun, keep it capitalized
      continue;
    }
    words[i] = words[i].toLowerCase();
  }

  return words.join(" ");
};

interface WritePostDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void; // Optional back navigation
  subject_matter?: string; // Title/subject - matches database schema
  post_content?: string; // Content - matches database schema
  post_digest?: string; // Short summary - matches database schema
  onArticleGenerated?: (article: { subject_matter: string; post_content: string }) => void;
}

export default function WritePostDrawer({ 
  isOpen, 
  onClose,
  onBack,
  subject_matter = '', 
  post_content = '',
  post_digest,
  onArticleGenerated 
}: WritePostDrawerProps) {
  const { currentWebsite } = useWebsites();
  const [title, setTitle] = useState(subject_matter);
  const [header, setHeader] = useState('');
  const [intro, setIntro] = useState(post_content);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedArticle, setGeneratedArticle] = useState<{ subject_matter: string; post_content: string } | null>(
    post_content ? { subject_matter, post_content } : null
  );
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(post_content || '');
  const [hasAttemptedGeneration, setHasAttemptedGeneration] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Update state when props change
  useEffect(() => {
    console.log('WritePostDrawer props changed:', { subject_matter, post_content, post_digest, isOpen });
    setTitle(subject_matter);
    setIntro(post_content);
    if (post_content) {
      setGeneratedArticle({ subject_matter, post_content });
      setEditedContent(post_content);
      setHasAttemptedGeneration(true);
    } else {
      setEditedContent(post_content);
      setHasAttemptedGeneration(false); // Reset so auto-generation can trigger
    }
  }, [subject_matter, post_content, post_digest]);

  // Update editedContent when generated article changes
  useEffect(() => {
    if (generatedArticle) {
      setEditedContent(generatedArticle.post_content);
    }
  }, [generatedArticle]);

  const handleGenerateArticle = async () => {
    if (!title.trim()) {
      setError('Please enter a title for your article');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Try multiple possible localStorage keys for website ID
      const websiteId = currentWebsite?.id || 
                       localStorage.getItem('onboardingWebsiteId') || 
                       localStorage.getItem('website_id') ||
                       localStorage.getItem('websiteId');
      
      const language = currentWebsite?.language || localStorage.getItem('website_language') || 'en';

      console.log('Website ID:', websiteId);
      console.log('Current website:', currentWebsite);
      console.log('Language:', language);
      console.log('All localStorage keys:', Object.keys(localStorage));

      if (!websiteId) {
        throw new Error('No website ID found');
      }

      // Format title based on language - Danish titles should not use newsletter capitalization
      let formattedTitle = title.trim();
      if (language === 'da') {
        formattedTitle = formatDanishTitle(formattedTitle);
        console.log('Danish title formatted:', { original: title.trim(), formatted: formattedTitle });
      }

      // Try to enrich with Planned Content metadata (if the idea was added)
      const calendarItems = JSON.parse(localStorage.getItem('calendar_items') || '[]');
      const matchedItem = Array.isArray(calendarItems) ? calendarItems.find((i: any) => (i?.title || '').trim() === formattedTitle) : undefined;

      // Build exact v3 onboarding payload with normalised structure
      const requestBody = prepareV3OnboardingRequest({
        title: formattedTitle,
        websiteId: String(websiteId),
        language,
        currentWebsiteUrl: currentWebsite?.url || null,
        websiteInfo: JSON.parse(localStorage.getItem('website_info') || '{}'),
        keyContentPages: JSON.parse(localStorage.getItem('key_content_pages') || '[]'),
        scrapedContent: JSON.parse(localStorage.getItem('scraped_content') || '[]'),
        ideaMeta: {
          content_type: matchedItem?.content_type || 'post',
          format_template: matchedItem?.format_template,
          length_tag: matchedItem?.length_tag,
          tone_style: matchedItem?.tone_style,
          keywords: matchedItem?.keywords,
          section_layout: matchedItem?.section_layout,
          element_settings: matchedItem?.element_settings
        }
      });

      console.log('Request body being sent:', JSON.stringify(requestBody, null, 2));

      console.log('Sending request to onboarding-generate-content:', requestBody);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/onboarding-generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      console.log('Onboarding generate content response:', data);
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${data.error || 'Request failed'}`);
      }
      
      if (data.error) {
        throw new Error(data.error);
      }

      if (data.content) {
        // Fallback for direct content response
        const article = {
          subject_matter: formattedTitle,
          post_content: data.content
        };
        setGeneratedArticle(article);
        onArticleGenerated?.(article);
      } else {
        console.error('No content in response:', data);
        throw new Error(`No content generated. Response: ${JSON.stringify(data)}`);
      }
    } catch (error) {
      console.error('Error generating article:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate article');
    } finally {
      setIsGenerating(false);
    }
  };

  // Auto-generate article when drawer opens with title
  useEffect(() => {
    console.log('Auto-generation check:', {
      isOpen,
      title: title.trim(),
      isGenerating,
      generatedArticle: !!generatedArticle,
      hasAttemptedGeneration,
      post_content: !!post_content
    });
    
    if (isOpen && title.trim() && !isGenerating && !generatedArticle && !hasAttemptedGeneration && !post_content) {
      console.log('Auto-generating article for title:', title);
      setHasAttemptedGeneration(true);
      handleGenerateArticle();
    } else {
      console.log('Auto-generation skipped - conditions not met', {
        isOpen,
        hasTitle: !!title.trim(),
        isGenerating,
        hasGeneratedArticle: !!generatedArticle,
        hasAttemptedGeneration,
        hasPostContent: !!post_content
      });
    }
  }, [isOpen, title, isGenerating, generatedArticle, hasAttemptedGeneration, post_content]);

  // Reset generation attempt when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setHasAttemptedGeneration(false);
    }
  }, [isOpen]);

  const handleSaveEdit = () => {
    if (generatedArticle) {
      setGeneratedArticle({
        ...generatedArticle,
        post_content: editedContent
      });
    }
    setIsEditing(false);
    toast.success('Content updated successfully');
  };

  const handleClose = () => {
    setTitle('');
    setHeader('');
    setIntro('');
    setGeneratedArticle(null);
    setError(null);
    setIsEditing(false);
    setEditedContent('');
    setHasAttemptedGeneration(false);
    onClose();
  };

  // Removed download helper as related UI actions were removed

  const handleAddToCalendar = async () => {
    if (!generatedArticle) return;
    
    const itemId = crypto.randomUUID();
    
    // Insert into post_themes database table for Planned Content
    let postThemeId: string | undefined;
    if (currentWebsite?.id) {
      try {
        const { data: postTheme, error: postThemeError } = await supabase
          .from('post_themes')
          .insert({
            website_id: currentWebsite.id,
            subject_matter: generatedArticle.subject_matter,
            keywords: [], // Default empty keywords
            status: 'generated',
            post_content: generatedArticle.post_content,
            post_digest: generatedArticle.post_content.substring(0, 200) + '...',
            content_type: 'post',
            format_template: 'guide',
            length_tag: 'long',
            tone_style: {},
            section_layout: [],
            element_settings: {}
          })
          .select()
          .single();

        if (postThemeError) {
          console.error('Error creating post theme:', postThemeError);
          toast.error('Failed to save article to database');
          return;
        } else {
          console.log('Successfully created post theme:', postTheme);
          postThemeId = postTheme.id;
        }
      } catch (error) {
        console.error('Error inserting post theme:', error);
        toast.error('Failed to save article to database');
        return;
      }
    }

    const item = {
      id: itemId,
      title: generatedArticle.subject_matter,
      digest: generatedArticle.post_content.substring(0, 200) + '...',
      fullContent: generatedArticle.post_content,
      isGenerated: true,
      postThemeId
    };
    const existingItems = JSON.parse(localStorage.getItem('calendar_items') || '[]');
    const updatedItems = [item, ...existingItems];
    localStorage.setItem('calendar_items', JSON.stringify(updatedItems));
    localStorage.setItem('calendar_enabled', '1');
    
    // Dispatch event to update calendar
    window.dispatchEvent(new CustomEvent('calendar-item-added'));
    
    toast.success('Article added to planned content');
    handleClose();
  };

  return (
    <AppDrawer isOpen={isOpen} onClose={handleClose} width="lg">
        <div>
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onBack || handleClose}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <FileText className="h-4 w-4 text-primary" />
              <h2 className="tracking-tight text-base font-medium text-foreground/80">
                {title || 'Write Full Article'}
              </h2>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

      <DrawerContent>
        {!generatedArticle ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-8">
            <div className="max-w-md">
              <h3 className="text-xl font-semibold mb-4">
                Ready to Generate Your Article
              </h3>
              <div className="text-muted-foreground mb-6">
                <p className="mb-3">
                  Once you've set your title and preferences, click "Generate Full Article" to create:
                </p>
                <ul className="text-left space-y-2">
                  <li>• Well-structured, engaging content</li>
                  <li>• Strategic backlinks to your website</li>
                  <li>• SEO-optimized for your target keywords</li>
                  <li>• Brand-consistent tone and messaging</li>
                </ul>
              </div>
              {isGenerating && (
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Generating Article...</span>
                </div>
              )}
              {!isGenerating && (
                <DrawerFooter>
                  <div className="flex items-center justify-end gap-2">
                    <Button onClick={handleGenerateArticle}>
                      Generate Full Article
                    </Button>
                  </div>
                </DrawerFooter>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Action Icons Row */}
            {generatedArticle && (
              <div className="flex items-center justify-end gap-1">
                <button
                  type="button"
                  onClick={() => setIsEditing(!isEditing)}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-10 w-10"
                  title={isEditing ? "Preview" : "Edit"}
                >
                  <IconEdit className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleGenerateArticle}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-10 w-10"
                  title="Regenerate"
                >
                  <IconRefreshCw className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Status */}
            <DrawerFooter>
              <div className="flex items-center justify-end gap-2">
                {isGenerating && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
            </DrawerFooter>

            {/* Content */}
            {isEditing ? (
              <div className="space-y-4">
                <Textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="min-h-[400px] resize-none"
                  placeholder="Edit your article content..."
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveEdit}>
                    Save Changes
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Digest in a nice card (like in planned content) */}
                {post_digest && (
                  <div className="p-3 bg-muted/50 rounded-md border border-muted">
                    <p className="text-sm text-muted-foreground leading-relaxed">{post_digest}</p>
                  </div>
                )}
                
                {/* Full article content */}
                <div>
                  <div
                    ref={contentRef}
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: editedContent
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </DrawerContent>
    </AppDrawer>
  );
}
